import React, { useState, useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';
import ResponsiveModal from '../../../common/ResponsiveModal';
import * as toolConfigService from '../../../../services/toolConfigService';
import * as userService from '../../../../services/userService';
import { syncAssistantToRetell, addCartesiaVoiceToRetell } from '../../../../services/retellSyncService';
import {
  RETELL_VOICE_MODEL_GROUPS, RETELL_VOICE_EMOTIONS, RETELL_VOICE_SPEED_RANGE, RETELL_VOLUME_RANGE,
  RETELL_VOICE_TEMPERATURE_RANGE,
} from '../../../../services/retellVoiceOptions';
import { CARTESIA_MODELS, CARTESIA_SPEED_RANGE, CARTESIA_VOLUME_RANGE } from '../../../../services/vapiVoiceConfig';
import toast from 'react-hot-toast';

const AssistantModal = ({ isOpen, assistant, onSave, onCancel, loading }) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [tools, setTools] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [retellStatus, setRetellStatus] = useState({ retell_agent_id: null, retell_synced_at: null, retell_sync_error: null });
  const [syncing, setSyncing] = useState(false);
  const [cartesiaImport, setCartesiaImport] = useState({ voiceId: '', name: '' });
  const [addingVoice, setAddingVoice] = useState(false);
  const [formData, setFormData] = useState({
    name: '', description: '', is_default: false, is_trial: false, trial_call_limit: 1, coach_id: '', first_message: '', voicemail_message: '', end_call_message: '',
    end_call_function_enabled: true, is_active: true, voice_provider: 'vapi', voice_id: 'Lily', voice_model: '', voice_speed: 1.0,
    voice_stability: '', voice_similarity_boost: '', voice_volume: '', retell_voice_id: '', retell_voice_model: '', retell_voice_speed: '', retell_volume: '',
    retell_voice_temperature: '', retell_voice_emotion: '', retell_model: '', model_provider: 'openai', model_name: 'gpt-4o-mini', model_temperature: 0.7,
    model_max_tokens: '', system_message: '', transcriber_provider: 'deepgram', transcriber_model: 'nova-2', transcriber_language: 'en',
    silence_timeout_seconds: 90, max_duration_seconds: 1800, background_sound: 'off', background_denoising_enabled: true,
    start_speaking_wait_seconds: 1.2, start_speaking_on_punctuation_seconds: 1.5, start_speaking_on_no_punctuation_seconds: 3.0,
    start_speaking_on_number_seconds: 0.8, stop_speaking_num_words: 1, stop_speaking_backoff_seconds: 4.0,
    retell_responsiveness: '', retell_interruption_sensitivity: '', retell_dynamic_responsiveness: false,
    retell_end_call_silence_seconds: '', retell_reminder_trigger_seconds: '', retell_reminder_max_count: '',
    retell_enable_backchannel: false, retell_backchannel_frequency: '',
    server_url: '', server_timeout_seconds: 20, server_messages: [], client_messages: [], tool_ids: [],
    hooks: []
  });

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen, assistant]);

  const loadData = async () => {
    const { data: toolList } = await toolConfigService.getTools({ isActive: true });
    setTools(toolList || []);
    const { data: coachList } = await userService.getCoaches();
    setCoaches(coachList || []);
    
    if (assistant) {
      setFormData({
        name: assistant.name || '', description: assistant.description || '', is_default: assistant.is_default || false,
        is_trial: assistant.is_trial || false, trial_call_limit: assistant.trial_call_limit || 1, coach_id: assistant.coach_id || '',
        first_message: assistant.first_message || '', voicemail_message: assistant.voicemail_message || '', end_call_message: assistant.end_call_message || '',
        end_call_function_enabled: assistant.end_call_function_enabled ?? true, is_active: assistant.is_active ?? true,
        voice_provider: assistant.voice_provider || 'vapi', voice_id: assistant.voice_id || 'Lily', voice_model: assistant.voice_model || '',
        voice_speed: assistant.voice_speed || 1.0, voice_stability: assistant.voice_stability || '',
        voice_similarity_boost: assistant.voice_similarity_boost || '', voice_volume: assistant.voice_volume || '', retell_voice_id: assistant.retell_voice_id || '', retell_voice_model: assistant.retell_voice_model || '',
        retell_voice_speed: assistant.retell_voice_speed ?? '', retell_volume: assistant.retell_volume ?? '',
        retell_voice_temperature: assistant.retell_voice_temperature ?? '', retell_voice_emotion: assistant.retell_voice_emotion || '',
        retell_model: assistant.retell_model || '', model_provider: assistant.model_provider || 'openai',
        model_name: assistant.model_name || 'gpt-4o-mini', model_temperature: assistant.model_temperature || 0.7,
        model_max_tokens: assistant.model_max_tokens || '', system_message: assistant.system_message || '',
        transcriber_provider: assistant.transcriber_provider || 'deepgram', transcriber_model: assistant.transcriber_model || 'nova-2',
        transcriber_language: assistant.transcriber_language || 'en', silence_timeout_seconds: assistant.silence_timeout_seconds || 90,
        max_duration_seconds: assistant.max_duration_seconds || 1800, background_sound: assistant.background_sound || 'off',
        background_denoising_enabled: assistant.background_denoising_enabled ?? true, start_speaking_wait_seconds: assistant.start_speaking_wait_seconds || 1.2,
        start_speaking_on_punctuation_seconds: assistant.start_speaking_on_punctuation_seconds || 1.5,
        start_speaking_on_no_punctuation_seconds: assistant.start_speaking_on_no_punctuation_seconds || 3.0,
        start_speaking_on_number_seconds: assistant.start_speaking_on_number_seconds || 0.8,
        stop_speaking_num_words: assistant.stop_speaking_num_words || 1, stop_speaking_backoff_seconds: assistant.stop_speaking_backoff_seconds || 4.0,
        // Retell dials: '' means "not set for this coach", which falls back to the
        // deployment default. ?? keeps a deliberate 0 from being blanked out.
        retell_responsiveness: assistant.retell_responsiveness ?? '',
        retell_interruption_sensitivity: assistant.retell_interruption_sensitivity ?? '',
        retell_dynamic_responsiveness: assistant.retell_dynamic_responsiveness ?? false,
        retell_end_call_silence_seconds: assistant.retell_end_call_silence_seconds ?? '',
        retell_reminder_trigger_seconds: assistant.retell_reminder_trigger_seconds ?? '',
        retell_reminder_max_count: assistant.retell_reminder_max_count ?? '',
        retell_enable_backchannel: assistant.retell_enable_backchannel ?? false,
        retell_backchannel_frequency: assistant.retell_backchannel_frequency ?? '',
        server_url: assistant.server_url || '', server_timeout_seconds: assistant.server_timeout_seconds || 20,
        server_messages: assistant.server_messages || [], client_messages: assistant.client_messages || [],
        tool_ids: assistant.tools?.map(t => t.id) || [],
        hooks: assistant.hooks || []
      });
      setRetellStatus({
        retell_agent_id: assistant.retell_agent_id || null,
        retell_synced_at: assistant.retell_synced_at || null,
        retell_sync_error: assistant.retell_sync_error || null,
      });
    } else {
      setRetellStatus({ retell_agent_id: null, retell_synced_at: null, retell_sync_error: null });
    }
    setActiveTab('basic');
  };

  const handleResync = async () => {
    if (!assistant?.id || syncing) return;
    setSyncing(true);
    const { data, error } = await syncAssistantToRetell(assistant.id);
    setSyncing(false);
    if (error) {
      toast.error(`Retell sync failed: ${error}`);
      setRetellStatus(prev => ({ ...prev, retell_sync_error: error }));
      return;
    }
    toast.success('Synced to Retell');
    setRetellStatus({
      retell_agent_id: data?.retell_agent_id || null,
      retell_synced_at: data?.synced_at || null,
      retell_sync_error: null,
    });
  };

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const handleAddCartesiaVoice = async () => {
    const voiceId = cartesiaImport.voiceId.trim();
    const voiceName = cartesiaImport.name.trim();
    if (!voiceId || !voiceName) { toast.error('Enter the Cartesia voice ID and a name'); return; }
    setAddingVoice(true);
    const { data, error } = await addCartesiaVoiceToRetell(voiceId, voiceName);
    setAddingVoice(false);
    if (error) {
      toast.error(`Could not add the voice: ${error}. Check that the voice is set to shared in Cartesia.`, { duration: 8000 });
      return;
    }
    handleChange('retell_voice_id', data.voice_id);
    setCartesiaImport({ voiceId: '', name: '' });
    toast.success('Voice added to Retell. Save the assistant to use it.');
  };
  // Model names aren't portable between providers: drop a Cartesia model when
  // leaving Cartesia, and anything that isn't one when switching to it.
  const handleVoiceProviderChange = (provider) => setFormData(prev => {
    const isCartesiaModel = CARTESIA_MODELS.includes(prev.voice_model);
    const keepModel = provider === 'cartesia' ? isCartesiaModel : !isCartesiaModel;
    return { ...prev, voice_provider: provider, voice_model: keepModel ? prev.voice_model : '' };
  });
  const cartesiaTuningAvailable = (formData.voice_model || '').startsWith('sonic-3');
  const cartesiaSpeed = Math.min(CARTESIA_SPEED_RANGE.max, Math.max(CARTESIA_SPEED_RANGE.min, Number(formData.voice_speed) || 1));
  const handleToolToggle = (toolId) => setFormData(prev => ({
    ...prev, tool_ids: prev.tool_ids.includes(toolId) ? prev.tool_ids.filter(id => id !== toolId) : [...prev.tool_ids, toolId]
  }));

  const handleHookChange = (index, field, value) => {
    const newHooks = [...(formData.hooks || [])];
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      newHooks[index] = { ...newHooks[index], [parent]: { ...newHooks[index][parent], [child]: value } };
    } else {
      newHooks[index] = { ...newHooks[index], [field]: value };
    }
    setFormData(prev => ({ ...prev, hooks: newHooks }));
  };

  const handleHookMessageChange = (hookIndex, msgIndex, value) => {
    const newHooks = [...(formData.hooks || [])];
    const newMessages = [...newHooks[hookIndex].do[0].exact];
    newMessages[msgIndex] = value;
    newHooks[hookIndex].do[0].exact = newMessages;
    setFormData(prev => ({ ...prev, hooks: newHooks }));
  };

  const addHookMessage = (hookIndex) => {
    const newHooks = [...(formData.hooks || [])];
    newHooks[hookIndex].do[0].exact.push('');
    setFormData(prev => ({ ...prev, hooks: newHooks }));
  };

  const removeHookMessage = (hookIndex, msgIndex) => {
    const newHooks = [...(formData.hooks || [])];
    newHooks[hookIndex].do[0].exact = newHooks[hookIndex].do[0].exact.filter((_, i) => i !== msgIndex);
    setFormData(prev => ({ ...prev, hooks: newHooks }));
  };

  const addHook = () => {
    const newHook = {
      on: "customer.speech.timeout",
      options: { timeoutSeconds: 10, triggerMaxCount: 3, triggerResetMode: "onUserSpeech" },
      do: [{ type: "say", exact: ["Are you still there?"] }],
      name: "idle_message_check"
    };
    setFormData(prev => ({ ...prev, hooks: [...(prev.hooks || []), newHook] }));
  };

  const removeHook = (index) => {
    setFormData(prev => ({ ...prev, hooks: prev.hooks.filter((_, i) => i !== index) }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { toast.error('Name required'); setActiveTab('basic'); return; }
    if (!formData.system_message.trim()) { toast.error('System message required'); setActiveTab('model'); return; }
    onSave({ ...formData, coach_id: formData.coach_id === '' ? null : formData.coach_id });
  };

  const tabs = [
    { id: 'basic', label: 'Basic', icon: '📝' },
    { id: 'voice', label: 'Voice', icon: '🎤' },
    { id: 'model', label: 'Model', icon: '🤖' },
    { id: 'transcriber', label: 'Transcriber', icon: '📝' },
    { id: 'advanced', label: 'Advanced', icon: '⚙️' }
  ];

  return (
    <ResponsiveModal isOpen={isOpen} onClose={onCancel} title={assistant ? 'Edit Assistant' : 'Create Assistant'} size="xl">
      <form onSubmit={handleSubmit} className="flex flex-col h-full">
        <div className="border-b px-6 pt-4">
          <nav className="-mb-px flex space-x-4">
            {tabs.map(t => (
              <button key={t.id} type="button" onClick={() => setActiveTab(t.id)}
                className={`py-2 px-1 border-b-2 text-sm font-medium ${activeTab === t.id ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500'}`}>
                {t.icon} {t.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {activeTab === 'basic' && (
            <>
              <input type="text" required value={formData.name} onChange={(e) => handleChange('name', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" placeholder="Assistant Name *" />
              <textarea value={formData.description} onChange={(e) => handleChange('description', e.target.value)} rows={2}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" placeholder="Description" />
              <select value={formData.is_default ? 'default' : formData.is_trial ? 'trial' : formData.coach_id || 'none'}
                onChange={(e) => {
                  if (e.target.value === 'default') {
                    handleChange('is_default', true);
                    handleChange('is_trial', false);
                    handleChange('trial_call_limit', null);
                    handleChange('coach_id', '');
                  }
                  else if (e.target.value === 'trial') {
                    handleChange('is_default', false);
                    handleChange('is_trial', true);
                    handleChange('trial_call_limit', 1);
                    handleChange('coach_id', '');
                  }
                  else if (e.target.value === 'none') {
                    handleChange('is_default', false);
                    handleChange('is_trial', false);
                    handleChange('trial_call_limit', null);
                    handleChange('coach_id', '');
                  }
                  else {
                    handleChange('is_default', false);
                    handleChange('is_trial', false);
                    handleChange('trial_call_limit', null);
                    handleChange('coach_id', e.target.value);
                  }
                }} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500">
                <option value="none">No assignment</option>
                <option value="default">Default for all</option>
                <option value="trial">Trial Assistant</option>
                {coaches.map(c => <option key={c.id} value={c.id}>Coach: {c.first_name} {c.last_name}</option>)}
              </select>
              
              {/* Trial Call Limit - Only shown for trial assistants */}
              {formData.is_trial && (
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Trial Call Limit
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.trial_call_limit || 1}
                    onChange={(e) => handleChange('trial_call_limit', parseInt(e.target.value) || 1)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="1"
                  />
                  <p className="text-xs text-gray-600 mt-1">
                    Maximum number of trial calls allowed per device/browser
                  </p>
                </div>
              )}
              <textarea value={formData.first_message} onChange={(e) => handleChange('first_message', e.target.value)} rows={2}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" placeholder="First Message" />
              <div className="grid grid-cols-2 gap-4">
                <input type="text" value={formData.voicemail_message} onChange={(e) => handleChange('voicemail_message', e.target.value)}
                  className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" placeholder="Voicemail Message" />
                <input type="text" value={formData.end_call_message} onChange={(e) => handleChange('end_call_message', e.target.value)}
                  className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" placeholder="End Contact Message" />
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="end_call_function_enabled"
                  checked={formData.end_call_function_enabled}
                  onChange={(e) => handleChange('end_call_function_enabled', e.target.checked)}
                  className="rounded focus:ring-2 focus:ring-indigo-500"
                />
                <label htmlFor="end_call_function_enabled" className="text-sm text-gray-700">
                  Enable end contact function (allows assistant to end contacts)
                </label>
              </div>
            </>
          )}

          {activeTab === 'voice' && (
            <>
              <section className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-4 space-y-4">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold text-gray-900">Vapi Voice</h3>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">Vapi only</span>
                </div>
              <div className="grid grid-cols-2 gap-4">
                <select value={formData.voice_provider} onChange={(e) => handleVoiceProviderChange(e.target.value)}
                  className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500">
                  <option value="vapi">Vapi</option><option value="11labs">ElevenLabs</option><option value="playht">PlayHT</option><option value="cartesia">Cartesia</option>
                </select>
                <input type="text" value={formData.voice_id} onChange={(e) => handleChange('voice_id', e.target.value)}
                  className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" placeholder="Voice ID" />
              </div>
              {formData.voice_provider === '11labs' && (
                <input type="text" value={formData.voice_model} onChange={(e) => handleChange('voice_model', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" placeholder="eleven_turbo_v2_5" />
              )}
              {formData.voice_provider === 'cartesia' ? (
                <>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Model</label>
                    <select value={formData.voice_model} onChange={(e) => handleChange('voice_model', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500">
                      <option value="">Default for this voice</option>
                      {CARTESIA_MODELS.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm">Speed: {cartesiaSpeed}</label>
                    <input type="range" min={CARTESIA_SPEED_RANGE.min} max={CARTESIA_SPEED_RANGE.max} step="0.05" value={cartesiaSpeed}
                      disabled={!cartesiaTuningAvailable}
                      onChange={(e) => handleChange('voice_speed', parseFloat(e.target.value))} className="w-full disabled:opacity-40" />
                  </div>
                  <div>
                    <label className="text-sm">Volume: {formData.voice_volume || 1}</label>
                    <input type="range" min={CARTESIA_VOLUME_RANGE.min} max={CARTESIA_VOLUME_RANGE.max} step="0.1" value={formData.voice_volume || 1}
                      disabled={!cartesiaTuningAvailable}
                      onChange={(e) => handleChange('voice_volume', parseFloat(e.target.value))} className="w-full disabled:opacity-40" />
                  </div>
                  {!cartesiaTuningAvailable && (
                    <p className="text-xs text-gray-500">Speed and volume need a sonic-3 model. Pick one above to use them.</p>
                  )}
                </>
              ) : (
                <div>
                  <label className="text-sm">Speed: {formData.voice_speed}</label>
                  <input type="range" min="0.5" max="2" step="0.1" value={formData.voice_speed}
                    onChange={(e) => handleChange('voice_speed', parseFloat(e.target.value))} className="w-full" />
                </div>
              )}
              {formData.voice_provider === '11labs' && (
                <>
                  <div>
                    <label className="text-sm">Stability: {formData.voice_stability || 0.5}</label>
                    <input type="range" min="0" max="1" step="0.05" value={formData.voice_stability || 0.5}
                      onChange={(e) => handleChange('voice_stability', parseFloat(e.target.value))} className="w-full" />
                  </div>
                  <div>
                    <label className="text-sm">Similarity: {formData.voice_similarity_boost || 0.75}</label>
                    <input type="range" min="0" max="1" step="0.05" value={formData.voice_similarity_boost || 0.75}
                      onChange={(e) => handleChange('voice_similarity_boost', parseFloat(e.target.value))} className="w-full" />
                  </div>
                </>
              )}
              </section>

              <section className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold text-gray-900">Retell Voice</h3>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Retell only</span>
                </div>
                <p className="text-xs text-gray-500">Leave any field blank to use the base Retell agent&apos;s setting. Retell&apos;s language model is on the Model tab.</p>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Retell Voice ID</label>
                  <input type="text" value={formData.retell_voice_id} onChange={(e) => handleChange('retell_voice_id', e.target.value)}
                    placeholder="e.g. 11labs-Adrian or custom_voice_..." className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 w-full" />
                </div>
                <div className="rounded-lg border border-emerald-200 bg-white/60 p-3 space-y-2">
                  <p className="text-sm text-gray-700">Add a Cartesia voice to Retell</p>
                  <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    <strong>Set the voice to shared in Cartesia first.</strong> Retell cannot reach a private voice, and adding it will fail.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <input type="text" value={cartesiaImport.voiceId} onChange={(e) => setCartesiaImport(prev => ({ ...prev, voiceId: e.target.value }))}
                      placeholder="Cartesia voice ID" className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 w-full" />
                    <input type="text" value={cartesiaImport.name} onChange={(e) => setCartesiaImport(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Name to show in Retell" maxLength={200} className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 w-full" />
                  </div>
                  <button type="button" onClick={handleAddCartesiaVoice} disabled={addingVoice}
                    className="px-3 py-1.5 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
                    {addingVoice ? 'Adding…' : 'Add to Retell and use this voice'}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Voice model</label>
                    <select value={formData.retell_voice_model} onChange={(e) => handleChange('retell_voice_model', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500">
                      <option value="">Base agent default</option>
                      {RETELL_VOICE_MODEL_GROUPS.map(g => (
                        <optgroup key={g.provider} label={g.provider}>
                          {g.models.map(m => <option key={m} value={m}>{m}</option>)}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Emotion</label>
                    <select value={formData.retell_voice_emotion} onChange={(e) => handleChange('retell_voice_emotion', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500">
                      <option value="">Base agent default</option>
                      {RETELL_VOICE_EMOTIONS.map(em => <option key={em} value={em}>{em}</option>)}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Retell supports emotion on Cartesia and MiniMax voices.</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Speed ({RETELL_VOICE_SPEED_RANGE.min}-{RETELL_VOICE_SPEED_RANGE.max})</label>
                    <input type="number" min={RETELL_VOICE_SPEED_RANGE.min} max={RETELL_VOICE_SPEED_RANGE.max} step="0.05" value={formData.retell_voice_speed}
                      onChange={(e) => handleChange('retell_voice_speed', e.target.value)} placeholder="Base default"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Volume ({RETELL_VOLUME_RANGE.min}-{RETELL_VOLUME_RANGE.max})</label>
                    <input type="number" min={RETELL_VOLUME_RANGE.min} max={RETELL_VOLUME_RANGE.max} step="0.1" value={formData.retell_volume}
                      onChange={(e) => handleChange('retell_volume', e.target.value)} placeholder="Base default"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Variation ({RETELL_VOICE_TEMPERATURE_RANGE.min}-{RETELL_VOICE_TEMPERATURE_RANGE.max})</label>
                    <input type="number" min={RETELL_VOICE_TEMPERATURE_RANGE.min} max={RETELL_VOICE_TEMPERATURE_RANGE.max} step="0.1" value={formData.retell_voice_temperature}
                      onChange={(e) => handleChange('retell_voice_temperature', e.target.value)} placeholder="Base default"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" />
                    <p className="text-xs text-gray-500 mt-1">Lower is steadier, higher is more varied.</p>
                  </div>
                </div>
              </section>
            </>
          )}

          {activeTab === 'model' && (
            <>
              <section className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-4 space-y-4">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold text-gray-900">Vapi Model</h3>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">Vapi only</span>
                </div>
              <div className="grid grid-cols-2 gap-4">
                <select value={formData.model_provider} onChange={(e) => handleChange('model_provider', e.target.value)}
                  className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500">
                  <option value="openai">OpenAI</option><option value="anthropic">Anthropic</option>
                </select>
                <select value={formData.model_name} onChange={(e) => handleChange('model_name', e.target.value)}
                  className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500">
                  {formData.model_provider === 'openai' ? (
                    <><option value="gpt-4o">GPT-4o</option><option value="gpt-4o-mini">GPT-4o Mini</option><option value="gpt-4-turbo">GPT-4 Turbo</option></>
                  ) : (
                    <><option value="claude-3-5-sonnet-20241022">Claude 3.5 Sonnet</option><option value="claude-3-opus-20240229">Claude 3 Opus</option></>
                  )}
                </select>
              </div>
              <input type="number" value={formData.model_max_tokens} onChange={(e) => handleChange('model_max_tokens', e.target.value ? parseInt(e.target.value) : '')}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" placeholder="Max Tokens (optional)" />
              </section>

              <section className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold text-gray-900">Retell Model</h3>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Retell only</span>
                </div>
                <p className="text-xs text-gray-500">Leave blank to use the base Retell agent&apos;s model.</p>
                <input type="text" value={formData.retell_model} onChange={(e) => handleChange('retell_model', e.target.value)}
                  placeholder="e.g. gpt-4.1-mini" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" />
              </section>

              <section className="rounded-lg border border-gray-300 p-4 space-y-4">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold text-gray-900">Prompt &amp; Temperature</h3>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-700">Both providers</span>
                </div>
                <textarea required value={formData.system_message} onChange={(e) => handleChange('system_message', e.target.value)} rows={16}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 font-mono text-sm" placeholder="System Message *" />
                <div>
                  <label className="text-sm">Temp: {formData.model_temperature}</label>
                  <input type="range" min="0" max="2" step="0.1" value={formData.model_temperature}
                    onChange={(e) => handleChange('model_temperature', parseFloat(e.target.value))} className="w-full" />
                </div>
              </section>
            </>
          )}

          {activeTab === 'transcriber' && (
            <section className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <h3 className="text-base font-semibold text-gray-900">Transcriber</h3>
                <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">Vapi only</span>
              </div>
              <p className="text-xs text-gray-500">Retell&apos;s transcription is configured on the base agent in the Retell dashboard.</p>
              <div className="grid grid-cols-3 gap-4">
              <select value={formData.transcriber_provider} onChange={(e) => handleChange('transcriber_provider', e.target.value)}
                className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500">
                <option value="deepgram">Deepgram</option><option value="assembly">AssemblyAI</option>
              </select>
              <select value={formData.transcriber_model} onChange={(e) => handleChange('transcriber_model', e.target.value)}
                className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500">
                {formData.transcriber_provider === 'deepgram' ? (
                  <><option value="nova-2">Nova 2</option><option value="nova">Nova</option></>
                ) : <option value="best">Best</option>}
              </select>
              <select value={formData.transcriber_language} onChange={(e) => handleChange('transcriber_language', e.target.value)}
                className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500">
                <option value="en">English</option><option value="es">Spanish</option>
              </select>
              </div>
            </section>
          )}

          {activeTab === 'advanced' && (
            <>
              {/* Settings below are grouped by which provider actually reads them:
                  Retell only uses max_duration_seconds and the retell_* columns. */}
              <section className="rounded-lg border border-gray-300 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-base font-semibold text-gray-900">Call Duration</h3>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-700">Both providers</span>
                </div>
                <p className="text-sm text-gray-600 mb-4">Applies whichever voice provider the call runs on.</p>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Maximum Duration
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      The maximum number of seconds a call will last. Range: 10-43200 seconds. Default: 1800 seconds.
                    </p>
                    <input
                      type="number"
                      min="10"
                      max="43200"
                      value={formData.max_duration_seconds}
                      onChange={(e) => handleChange('max_duration_seconds', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="1800"
                    />
                  </div>
                </div>
              </section>

              <section className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-4 space-y-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-base font-semibold text-gray-900">Vapi Call Settings</h3>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">Vapi only</span>
                  </div>
                  <p className="text-sm text-gray-600">Ignored on Retell calls. Retell&apos;s equivalents are in the green section below.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Silence Timeout</label>
                  <p className="text-xs text-gray-500 mb-2">
                    How long to wait before a call is automatically ended due to inactivity. Range: 10-3600 seconds. Default: 90 seconds.
                  </p>
                  <input
                    type="number"
                    min="10"
                    max="3600"
                    value={formData.silence_timeout_seconds}
                    onChange={(e) => handleChange('silence_timeout_seconds', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="90"
                  />
                </div>

                <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Background Sound</label>
                  <select value={formData.background_sound} onChange={(e) => handleChange('background_sound', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500">
                    <option value="off">No Background</option><option value="office">Office</option><option value="cafe">Cafe</option>
                  </select>
                </div>
              </div>
              <p className="text-sm font-medium">Start Speaking</p>
              <div className="grid grid-cols-2 gap-4">
                {['wait_seconds', 'on_punctuation_seconds', 'on_no_punctuation_seconds', 'on_number_seconds'].map(f => (
                  <div key={f}>
                    <label className="text-xs text-gray-500">{f.replace(/_/g, ' ').replace('start speaking ', '')}</label>
                    <input type="number" step="0.1" value={formData[`start_speaking_${f}`]}
                      onChange={(e) => handleChange(`start_speaking_${f}`, parseFloat(e.target.value))}
                      className="w-full px-3 py-2 border rounded-lg" placeholder="0.0" />
                  </div>
                ))}
              </div>
              <p className="text-sm font-medium">Stop Speaking</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500">Num Words</label>
                  <input type="number" value={formData.stop_speaking_num_words} onChange={(e) => handleChange('stop_speaking_num_words', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border rounded-lg" placeholder="1" />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Backoff Seconds</label>
                  <input type="number" step="0.1" value={formData.stop_speaking_backoff_seconds} onChange={(e) => handleChange('stop_speaking_backoff_seconds', parseFloat(e.target.value))}
                    className="w-full px-3 py-2 border rounded-lg" placeholder="4.0" />
                </div>
              </div>
              <p className="text-sm font-medium border-t pt-4">Idle Messages (Hooks)</p>
              <div className="space-y-4">
                {(formData.hooks || []).map((hook, idx) => (
                  <div key={idx} className="border rounded-lg p-3 space-y-3 bg-gray-50">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium text-gray-500">Hook #{idx + 1}</span>
                      <button type="button" onClick={() => removeHook(idx)} className="text-red-500 hover:text-red-700 text-xs">Remove Hook</button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-gray-500">Timeout (s)</label>
                        <input type="number" value={hook.options.timeoutSeconds} onChange={(e) => handleHookChange(idx, 'options.timeoutSeconds', parseFloat(e.target.value))}
                          className="w-full px-3 py-2 border rounded-lg text-sm" />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Max Count</label>
                        <input type="number" value={hook.options.triggerMaxCount} onChange={(e) => handleHookChange(idx, 'options.triggerMaxCount', parseInt(e.target.value))}
                          className="w-full px-3 py-2 border rounded-lg text-sm" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Messages (Randomly selected)</label>
                      <div className="space-y-2 mt-1">
                        {hook.do[0].exact.map((msg, msgIdx) => (
                          <div key={msgIdx} className="flex space-x-2">
                            <input type="text" value={msg} onChange={(e) => handleHookMessageChange(idx, msgIdx, e.target.value)}
                              className="flex-1 px-3 py-2 border rounded-lg text-sm" placeholder="Message..." />
                            <button type="button" onClick={() => removeHookMessage(idx, msgIdx)} className="text-red-500 hover:text-red-700">×</button>
                          </div>
                        ))}
                        <button type="button" onClick={() => addHookMessage(idx)} className="text-xs text-indigo-600 hover:text-indigo-800">+ Add Message Option</button>
                      </div>
                    </div>
                  </div>
                ))}
                <button type="button" onClick={addHook} className="text-sm text-indigo-600 hover:text-indigo-800">+ Add Idle Hook</button>
              </div>

              <p className="text-sm font-medium border-t pt-4">Server Configuration</p>
              <div className="grid grid-cols-1 gap-2">
                <label className="text-xs text-gray-500">Server URL (optional)</label>
                <input type="url" value={formData.server_url} onChange={(e) => handleChange('server_url', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg" placeholder="https://..." />
                
                <label className="text-xs text-gray-500">Server Timeout (seconds)</label>
                <input type="number" value={formData.server_timeout_seconds} onChange={(e) => handleChange('server_timeout_seconds', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border rounded-lg" placeholder="20" />
              </div>
              
              <p className="text-sm font-medium border-t pt-4">Server Messages (sent to webhook)</p>
              <div className="space-y-2 max-h-40 overflow-y-auto border rounded p-2">
                {['assistant.started', 'conversation-update', 'transfer-update', 'end-of-call-report', 'function-call', 'hang', 'model-output', 'phone-call-control', 'speech-update', 'status-update', 'transcript', 'Transcript (Final)', 'tool-calls', 'transfer-destination-request', 'handoff-destination-request', 'user-interrupted', 'voice-input', 'chat.created', 'chat.deleted', 'session.created', 'session.updated', 'session.deleted'].map(msg => (
                  <label key={msg} className="flex items-center space-x-2 hover:bg-gray-50 p-1 rounded">
                    <input type="checkbox"
                      checked={formData.server_messages.includes(msg)}
                      onChange={(e) => handleChange('server_messages',
                        e.target.checked ? [...formData.server_messages, msg] : formData.server_messages.filter(m => m !== msg)
                      )}
                      className="rounded" />
                    <span className="text-xs">{msg}</span>
                  </label>
                ))}
              </div>

              <p className="text-sm font-medium border-t pt-4">Client Messages (sent to SDK)</p>
              <div className="space-y-2 max-h-40 overflow-y-auto border rounded p-2">
                {['conversation-update', 'function-call', 'function-call-result', 'hang', 'metadata', 'model-output', 'speech-update', 'status-update', 'transcript', 'tool-calls', 'tool-calls-result', 'tool.completed', 'transfer-update', 'user-interrupted', 'voice-input', 'workflow.node.started', 'assistant.started'].map(msg => (
                  <label key={msg} className="flex items-center space-x-2 hover:bg-gray-50 p-1 rounded">
                    <input type="checkbox"
                      checked={formData.client_messages.includes(msg)}
                      onChange={(e) => handleChange('client_messages',
                        e.target.checked ? [...formData.client_messages, msg] : formData.client_messages.filter(m => m !== msg)
                      )}
                      className="rounded" />
                    <span className="text-xs">{msg}</span>
                  </label>
                ))}
              </div>

              </section>

              <section className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-4 space-y-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-base font-semibold text-gray-900">Retell Turn-Taking</h3>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Retell only</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Ignored on Vapi calls. Retell replaces the Vapi start/stop-speaking timings above with two 0–1 dials.
                    Leave a field blank to use the deployment default.
                  </p>
                  {assistant && (
                    <div className="mt-2 flex items-center justify-between gap-3 rounded border border-emerald-100 bg-white/60 px-3 py-2">
                      <div>
                        {/*
                          The happy path needs an agent id AND no recorded error:
                          retell_synced_at alone would still read "Synced" after a
                          call-time fallback marked this coach's agent broken.
                        */}
                        {retellStatus.retell_sync_error ? (
                          <p className="text-xs text-red-600">Last sync failed: {retellStatus.retell_sync_error}</p>
                        ) : retellStatus.retell_agent_id && retellStatus.retell_synced_at ? (
                          <p className="text-xs text-emerald-700">
                            Synced {formatDistanceToNow(new Date(retellStatus.retell_synced_at), { addSuffix: true })}
                          </p>
                        ) : retellStatus.retell_agent_id ? (
                          <p className="text-xs text-amber-600">
                            Agent linked, but no completed sync is recorded — re-sync to be sure.
                          </p>
                        ) : (
                          <p className="text-xs text-gray-500">Not synced — calls use the base agent.</p>
                        )}
                      </div>
                      <button type="button" onClick={handleResync} disabled={syncing}
                        className="shrink-0 px-3 py-1.5 text-xs font-medium border border-emerald-300 rounded-lg text-emerald-700 hover:bg-emerald-100 disabled:opacity-50">
                        {syncing ? 'Syncing…' : 'Re-sync to Retell'}
                      </button>
                    </div>
                  )}
                </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500">Responsiveness (0–1)</label>
                  <input type="number" step="0.05" min="0" max="1" value={formData.retell_responsiveness}
                    onChange={(e) => handleChange('retell_responsiveness', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg" placeholder="default 1.0" />
                  <p className="text-xs text-gray-400 mt-1">Lower waits longer before the agent answers, so a pause mid-thought doesn&apos;t get cut off.</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Interruption Sensitivity (0–1)</label>
                  <input type="number" step="0.05" min="0" max="1" value={formData.retell_interruption_sensitivity}
                    onChange={(e) => handleChange('retell_interruption_sensitivity', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg" placeholder="default 1.0" />
                  <p className="text-xs text-gray-400 mt-1">Lower makes it harder for the user to cut the agent off mid-sentence.</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">End Call After Silence (seconds)</label>
                  <input type="number" min="10" value={formData.retell_end_call_silence_seconds}
                    onChange={(e) => handleChange('retell_end_call_silence_seconds', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg" placeholder="default 600" />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Reminder After Silence (seconds)</label>
                  <input type="number" step="0.5" value={formData.retell_reminder_trigger_seconds}
                    onChange={(e) => handleChange('retell_reminder_trigger_seconds', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg" placeholder="default 10" />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Max Reminders</label>
                  <input type="number" min="0" max="10" value={formData.retell_reminder_max_count}
                    onChange={(e) => handleChange('retell_reminder_max_count', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg" placeholder="default 1" />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Backchannel Frequency (0–1)</label>
                  <input type="number" step="0.05" min="0" max="1" value={formData.retell_backchannel_frequency}
                    onChange={(e) => handleChange('retell_backchannel_frequency', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg" placeholder="default 0.8" />
                </div>
              </div>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={!!formData.retell_dynamic_responsiveness}
                    onChange={(e) => handleChange('retell_dynamic_responsiveness', e.target.checked)} />
                  Dynamic responsiveness (adapts to the user&apos;s pace)
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={!!formData.retell_enable_backchannel}
                    onChange={(e) => handleChange('retell_enable_backchannel', e.target.checked)} />
                  Backchannel (&quot;mm-hmm&quot; while the user talks)
                </label>
              </div>
              </section>

              <section className="rounded-lg border border-gray-300 p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold text-gray-900">Tools</h3>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">Vapi only</span>
                </div>
                <p className="text-xs text-gray-500">On Retell, tools are configured on the base agent in the Retell dashboard.</p>
              <div className="border rounded-lg p-4 max-h-48 overflow-y-auto">
                {tools.length === 0 ? <p className="text-sm text-gray-500 text-center">No tools available</p> : (
                  <div className="space-y-2">
                    {tools.map(t => (
                      <label key={t.id} className="flex items-center space-x-2 cursor-pointer">
                        <input type="checkbox" checked={formData.tool_ids.includes(t.id)} onChange={() => handleToolToggle(t.id)}
                          className="rounded" />
                        <span className="text-sm">{t.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              </section>
            </>
          )}
        </div>

        <div className="border-t px-6 py-4 flex justify-end space-x-3">
          <button type="button" onClick={onCancel} disabled={loading} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
          <button type="submit" disabled={loading} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
            {loading ? 'Saving...' : (assistant ? 'Update' : 'Create')}
          </button>
        </div>
      </form>
    </ResponsiveModal>
  );
};

export default AssistantModal;